import React from "react";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../Tooltip";

interface ContextHelpProps {
  ariaLabel: string;
  whatIs: string;
  formula?: string;
  period?: string;
  note?: string;
  noteClass?: string;
  className?: string;
}

const ContextHelpContent = ({
  whatIs,
  formula,
  period,
  note,
  noteClass,
}: ContextHelpProps) => (
  <div className="space-y-2">
    <p className="text-[12px] leading-5 text-gray-100">{whatIs}</p>
    {formula || period || note ? (
      <div className="space-y-1 border-t border-white/10 pt-2">
        {formula ? (
          <p className="text-[11px] leading-4 text-gray-300">{formula}</p>
        ) : null}
        {period ? (
          <p className="text-[11px] leading-4 text-gray-300">{period}</p>
        ) : null}
        {note ? (
          <p
            className={`text-[11px] leading-4 ${noteClass || "text-gray-300"}`}
          >
            {note}
          </p>
        ) : null}
      </div>
    ) : null}
  </div>
);

type TriggerButtonProps = Pick<ContextHelpProps, "ariaLabel" | "className"> &
  Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "aria-label" | "className"
  >;

const TriggerButton = React.forwardRef<HTMLButtonElement, TriggerButtonProps>(
  ({ ariaLabel, className = "", type = "button", ...buttonProps }, ref) => (
    <button
      ref={ref}
      type={type}
      aria-label={ariaLabel}
      className={`inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-500/70 bg-gray-800/90 text-gray-300 shadow-[0_0_0_1px_rgba(255,255,255,0.02)] transition-all duration-150 hover:-translate-y-px hover:border-cyan-400/60 hover:bg-cyan-500/10 hover:text-cyan-200 focus-visible:ring-2 focus-visible:ring-cyan-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 focus-visible:outline-none ${className}`}
      {...buttonProps}
    >
      <Info className="h-3 w-3" />
    </button>
  ),
);
TriggerButton.displayName = "ContextHelpTriggerButton";

const ContextHelp: React.FC<ContextHelpProps> = (props) => {
  if (!props.whatIs) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <TriggerButton
            ariaLabel={props.ariaLabel}
            className={props.className}
          />
        </TooltipTrigger>
        <TooltipContent side="top" align="center" sideOffset={10}>
          <ContextHelpContent {...props} />
          <TooltipArrow className="fill-gray-900/95" />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export { ContextHelp };
